import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
    // Base styles shared across ALL variants
    [
        'inline-flex items-center justify-center gap-2 shrink-0',
        'font-bold rounded-xl transition-all duration-150 ease-in-out',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-primary/20 focus-visible:border-teal-primary',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        'select-none',
    ],
    {
        variants: {
            variant: {
                // Primary CTA — teal, high emphasis
                primary:
                    'bg-teal-primary text-white hover:opacity-90 active:bg-teal-dark active:scale-[0.98] premium-shadow',
                // Secondary — neutral, moderate emphasis
                secondary:
                    'bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 active:scale-[0.98]',
                // Outline — white bg with border, low emphasis
                outline:
                    'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 active:bg-slate-100 active:scale-[0.98]',
                // Ghost — no bg at rest, lowest emphasis
                ghost:
                    'bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200 active:scale-[0.98]',
                // Destructive — red, for delete/danger actions
                destructive:
                    'bg-red-600 text-white hover:bg-red-500 active:bg-red-700 active:scale-[0.98]',
                // Quiet destructive — ghost-style for inline delete
                'destructive-ghost':
                    'bg-transparent text-red-500 hover:bg-red-50 active:bg-red-100 active:scale-[0.98]',
            },
            size: {
                sm: 'h-8 px-3 text-xs rounded-lg',
                md: 'h-10 px-4 text-sm',
                lg: 'h-12 px-6 text-base rounded-2xl',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
        },
    }
);

interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    isLoading?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
}

/**
 * Button — Skuulbe's strictly typed interaction primitive.
 * Use this instead of any <button> or raw Tailwind class combination.
 *
 * @example
 * <Button variant="primary" size="md" isLoading={submitting}>Save</Button>
 * <Button variant="destructive-ghost" startIcon={<Trash2 />}>Delete</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant,
            size,
            isLoading = false,
            startIcon,
            endIcon,
            className,
            children,
            disabled,
            ...rest
        },
        ref
    ) => {
        return (
            <button
                ref={ref}
                disabled={disabled || isLoading}
                className={cn(buttonVariants({ variant, size }), className)}
                {...rest}
            >
                {isLoading ? (
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" />
                ) : (
                    startIcon && <span className="shrink-0">{startIcon}</span>
                )}
                {children}
                {endIcon && !isLoading && <span className="shrink-0">{endIcon}</span>}
            </button>
        );
    }
);

Button.displayName = 'Button';

export { buttonVariants };
